import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown, Star, CalendarCheck, MessageCircle, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { whatsappLink, telLink, mapsLink } from "@/lib/contact";

const HERO_POSTER = "/videos/hero-poster.jpg";
const CLIPS = [
  "/videos/hero-1-ppf.mp4",       // PPF / koruyucu film uygulaması
  "/videos/hero-2-buffing.mp4",   // makine ile polisaj
  "/videos/hero-3-headlight.mp4", // far / aydınlatma close-up
  "/videos/hero-4-water.mp4",     // su boncuklanması
  "/videos/hero-5-interior.mp4",  // iç mekan / deri temizlik
];

/**
 * Cinematic two-layer crossfading video montage.
 * Only two clips are ever loaded at a time (active + preloaded next).
 */
function HeroMontage() {
  const reduceMotion = useReducedMotion();
  const refs = [useRef(null), useRef(null)];
  const [active, setActive] = useState(0);
  const [srcs, setSrcs] = useState([CLIPS[0], CLIPS[1]]);
  const nextIdx = useRef(2);

  useEffect(() => {
    if (reduceMotion) return;
    const v = refs[active].current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, srcs, reduceMotion]);

  const handleEnded = (layer) => {
    if (layer !== active) return;
    const other = layer === 0 ? 1 : 0;
    setSrcs((prev) => {
      const copy = [...prev];
      copy[layer] = CLIPS[nextIdx.current % CLIPS.length];
      nextIdx.current += 1;
      return copy;
    });
    setActive(other);
  };

  if (reduceMotion) {
    return <img src={HERO_POSTER} alt="Premium araç detaylandırma" className="h-full w-full object-cover" />;
  }

  return (
    <>
      {srcs.map((src, i) => (
        <video
          key={i}
          ref={refs[i]}
          src={src}
          poster={HERO_POSTER}
          muted
          playsInline
          autoPlay={i === 0}
          preload="auto"
          onEnded={() => handleEnded(i)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-out ${
            active === i ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </>
  );
}

export default function Hero() {
  const settings = useSettings();
  const wa = whatsappLink(settings, "Merhaba, araç koruma hizmetleri hakkında bilgi almak istiyorum.");
  const tel = telLink(settings);
  const maps = mapsLink(settings);

  return (
    <section data-testid="hero-section" className="relative grain flex min-h-[100svh] items-center overflow-hidden bg-neutral-950">
      {/* Cinematic video montage */}
      <div className="absolute inset-0">
        <HeroMontage />
        {/* Readability + mood overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/80 to-neutral-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/10 to-neutral-950/70" />
        {/* Subtle gold light accent */}
        <div className="pointer-events-none absolute -left-40 top-1/4 h-[28rem] w-[28rem] rounded-full bg-gold/10 blur-[140px]" />
      </div>

      {/* Thin gold top accent line */}
      <div className="absolute inset-x-0 top-0 z-10 h-px gold-divider opacity-60" />

      <div className="container-x relative z-10 pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 backdrop-blur-sm">
            <Star className="h-3.5 w-3.5 fill-gold text-gold" />
            <span className="text-xs font-semibold tracking-wide text-gold">Kocaeli'nin ilk ve lider Meguiar's bakım merkezi</span>
          </div>

          <h1 className="mt-6 font-display text-3xl font-semibold leading-[1.08] tracking-tight text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.45)] sm:text-4xl lg:text-5xl">
            Kocaeli'nin Premium <br className="hidden sm:block" />
            <span className="text-gold-gradient">Araç Koruma Merkezi</span>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-relaxed text-neutral-200 sm:text-lg">
            Seramik Kaplama, PPF Kaplama, Boya Koruma ve profesyonel araç detaylandırma hizmetleriyle aracınız korunur, yenilenir ve değer kazanır.
          </p>

          {/* Primary CTAs */}
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link
              to="/randevu"
              data-testid="cta-hero-randevu"
              className="group inline-flex items-center justify-center gap-2.5 rounded-xl bg-gold px-7 py-4 text-sm font-bold text-black shadow-[0_10px_30px_-8px_rgba(245,197,66,0.6)] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-300"
            >
              <CalendarCheck className="h-[18px] w-[18px]" /> Ücretsiz Randevu Al
            </Link>
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-hero-whatsapp"
              className="group inline-flex items-center justify-center gap-2.5 rounded-xl border border-white/15 px-7 py-4 text-sm font-semibold text-white glass-light transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold/50 hover:bg-white/[0.08]"
            >
              <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp'tan Bilgi Al
            </a>
          </div>

          {/* Secondary ghost actions */}
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-400">
            <a href={tel} data-testid="cta-hero-call" className="inline-flex items-center gap-2 transition-colors hover:text-white">
              <Phone className="h-4 w-4 text-gold" /> Telefonla Ara
            </a>
            <a href={maps} target="_blank" rel="noopener noreferrer" data-testid="cta-hero-maps" className="inline-flex items-center gap-2 transition-colors hover:text-white">
              <MapPin className="h-4 w-4 text-gold" /> Yol Tarifi
            </a>
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-neutral-400">
            <div className="flex items-center gap-2">
              <span className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-gold text-gold" />)}</span>
              <span>Yüzlerce memnun müşteri</span>
            </div>
            <div className="hidden h-4 w-px bg-white/20 sm:block" />
            <span>Randevulu • Profesyonel ürünler • Uzman uygulama</span>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 lg:block">
        <ChevronDown className="h-6 w-6 animate-bounce text-white/40" />
      </div>
    </section>
  );
}
